import axios from 'axios';
import {
  clearFinanceUnlockToken,
  getFinanceUnlockToken,
} from './financeLockSession';

function resolveApiBaseUrl() {
  const configuredUrl = String(process.env.REACT_APP_API_URL || '').trim();

  if (configuredUrl) {
    return configuredUrl;
  }

  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'Production config error: REACT_APP_API_URL is required and must point to https://api.dektal360pro.com/api.'
    );
  }

  console.warn('REACT_APP_API_URL is not set. Using local development API at http://localhost:5000/api.');
  return 'http://localhost:5000/api';
}

export const API_BASE_URL = resolveApiBaseUrl();

const SUBSCRIPTION_BLOCK_CODES = new Set([
  'SUBSCRIPTION_INACTIVE',
  'SUBSCRIPTION_EXPIRED',
  'SUBSCRIPTION_PAST_DUE',
  'SUBSCRIPTION_CANCELLED',
  'SUBSCRIPTION_PENDING',
  'SUBSCRIPTION_REQUIRED',
]);

export function isPlatformAdminSession() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.role === 'platform_admin';
  } catch (_error) {
    return false;
  }
}

export function shouldRedirectToSubscriptionRenewal(status, code) {
  return (
    (status === 402 || status === 403) &&
    SUBSCRIPTION_BLOCK_CODES.has(code) &&
    !isPlatformAdminSession()
  );
}

const instance = axios.create({
  baseURL: API_BASE_URL,
});

instance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const financeUnlockToken = getFinanceUnlockToken();
  if (financeUnlockToken) {
    config.headers['x-finance-unlock-token'] = financeUnlockToken;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code;

    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      clearFinanceUnlockToken();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    if (status === 423 && code === 'FINANCE_LOCK_REQUIRED') {
      clearFinanceUnlockToken();
    }

    if (shouldRedirectToSubscriptionRenewal(status, code)) {
      clearFinanceUnlockToken();
      if (!window.location.pathname.startsWith('/subscription')) {
        window.location.href = `/subscription/renew?reason=${encodeURIComponent(code)}`;
      }
    }

    return Promise.reject(error);
  }
);

export const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${API_BASE_URL.replace(/\/api$/, '')}${path}`;
  return `${API_BASE_URL}/${path}`;
};

export default instance;
